import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, Loader2, MessageCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Digite seu nome');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Digite seu email');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    try {
      await register(
        formData.email,
        formData.password,
        formData.name,
        'Portuguese',
        'English'
      );
      toast.success('Conta criada! 🎉');
      navigate('/onboarding');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao criar conta';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <div className="bg-dark-card rounded-3xl p-6 border border-dark-border shadow-dark-medium">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-vocalira/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-vocalira/30">
              <MessageCircle className="w-8 h-8 text-vocalira" />
            </div>
            <h1 className="font-fraunces text-2xl font-semibold text-dark-text">
              Criar Conta Grátis
            </h1>
            <p className="text-dark-muted text-sm mt-2 flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 text-cyan" />
              Seu companheiro diário de conversação
            </p>
          </div>

          {/* Social Login Buttons */}
          <div className="space-y-3 mb-5">
            <Button
              type="button"
              onClick={handleGoogleRegister}
              variant="outline"
              className="w-full bg-dark-elevated border-dark-border text-dark-text hover:bg-dark-border rounded-xl py-5 font-medium flex items-center justify-center gap-3"
              data-testid="google-register-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar com Google
            </Button>

            <Button
              type="button"
              disabled
              variant="outline"
              className="w-full bg-dark-elevated border-dark-border text-dark-muted rounded-xl py-5 font-medium flex items-center justify-center gap-3 opacity-50 cursor-not-allowed"
              data-testid="apple-register-btn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Continuar com Apple (em breve)
            </Button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-5">
            <div className="flex-1 h-px bg-dark-border"></div>
            <span className="text-dark-muted text-sm">ou com email</span>
            <div className="flex-1 h-px bg-dark-border"></div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="text"
                data-testid="register-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-dark-elevated border-dark-border text-dark-text rounded-xl px-4 py-3 h-12 focus:ring-2 focus:ring-vocalira/30 focus:border-vocalira placeholder:text-dark-muted"
                placeholder="Seu nome"
                autoComplete="name"
              />
            </div>

            <div>
              <Input
                type="email"
                data-testid="register-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-dark-elevated border-dark-border text-dark-text rounded-xl px-4 py-3 h-12 focus:ring-2 focus:ring-vocalira/30 focus:border-vocalira placeholder:text-dark-muted"
                placeholder="Seu email"
                autoComplete="email"
              />
            </div>

            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                data-testid="register-password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-dark-elevated border-dark-border text-dark-text rounded-xl px-4 py-3 h-12 pr-12 focus:ring-2 focus:ring-vocalira/30 focus:border-vocalira placeholder:text-dark-muted"
                placeholder="Senha (mín. 6 caracteres)"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-dark-muted hover:text-dark-text transition-colors"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <Button
              type="submit"
              data-testid="register-submit-btn"
              disabled={loading}
              className="w-full bg-vocalira-gradient text-white hover:opacity-90 rounded-full h-12 font-medium shadow-glow"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Criando...
                </>
              ) : (
                'Começar a Conversar'
              )}
            </Button>
          </form>

          {/* Benefits */}
          <div className="mt-5 p-4 bg-dark-elevated rounded-xl border border-dark-border">
            <p className="text-dark-muted text-xs text-center">
              ✨ Pratique conversação com a Lira, sua tutora de IA, em apenas 5 minutos por dia
            </p>
          </div>

          {/* Login Link */}
          <div className="mt-5 text-center">
            <p className="text-sm text-dark-muted">
              Já tem conta?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-vocalira font-medium hover:text-vocalira-light transition-colors"
                data-testid="go-to-login-btn"
              >
                Entrar
              </button>
            </p>
          </div>

          {/* Legal */}
          <p className="mt-4 text-center text-xs text-dark-muted/60">
            Ao criar conta, você aceita os{' '}
            <a href="/terms" className="underline hover:text-dark-muted">Termos</a>
            {' '}e{' '}
            <a href="/privacy" className="underline hover:text-dark-muted">Privacidade</a>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
